// src/pages/AllGroups.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import {
  SearchOutlined,
  GroupAddOutlined,
  HourglassTop,
  CheckCircle,
  CalendarMonth,
  PeopleAlt,
  AttachMoney,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { getData, postData } from "../services/request";

// Helper Functions
const toGroups = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.groups)) return response.groups;
  return response?.id ? [response] : [];
};

const isGroupActive = (group) => group?.isActive !== false;
const groupKey = (group, index) => group?.id || `${group?.title}-${index}`;

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getStoredUser = () =>
  JSON.parse(localStorage.getItem("userData") || "{}");
const getUserRole = (user) => user?.userType || "groupUser";

const JoinedGroups = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [joinedGroups, setJoinedGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);

  const user = getStoredUser();
  const role = getUserRole(user);
  const displayName = user?.fullName || user?.full_name || "Dajoo member";
  const storedUser =
    user || JSON.parse(localStorage.getItem("userData") || "{}");
  const symbol =
    storedUser?.country?.symbol || storedUser?.country?.currency || "NGN";
  const formatCurrency = (value, symbol) => {
    const numericValue = Number(value || 0);
    return `${symbol} ${numericValue.toLocaleString("en-US")}`;
  };

  const goTo = (path) => {
    setMobileNavOpen(false);
    navigate(path);
  };

  const logout = () => {
    ["accessToken", "userData", "deviceId", "notifications", "nin"].forEach(
      (key) => localStorage.removeItem(key),
    );
    navigate("/", { replace: true });
  };

  // ✅ Reusable Fetch Function
  const fetchAllData = async () => {
    setLoading(true);
    setError(false);
    try {
      const [allResponse, joinedResponse] = await Promise.all([
        getData("/api/group/all"),
        getData("/api/group/fetch_joined"),
      ]);

      const allGroups = toGroups(allResponse);
      const joinedGroupsData = toGroups(joinedResponse);

      // ✅ Logic: Sirf 'Approved' wale groups filter karein
      const approvedGroupIds = new Set(
        joinedGroupsData
          .filter((item) => item?.status === "Approved")
          .map((item) => item?.groupId || item?.id),
      );

      const filteredGroups = allGroups.filter((group) =>
        approvedGroupIds.has(group.id),
      );

      setGroups(filteredGroups);
      setJoinedGroups(joinedGroupsData);
    } catch (error) {
      console.error(error);
      setGroups([]);
      setJoinedGroups([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const getJoinStatus = (groupId) => {
    const joinedGroup = joinedGroups.find((g) => g.groupId === groupId);
    if (!joinedGroup) return "none";
    if (joinedGroup.status === "Panding" || joinedGroup.status === "Pending") {
      return "pending";
    }
    return "joined";
  };

  const handleJoinClick = async (group) => {
    const storedUserData = JSON.parse(localStorage.getItem("userData") || "{}");
    const insuredStatus = Number(storedUserData?.isInsured ?? 0);

    if (insuredStatus === 0) {
      const nin = JSON.parse(localStorage.getItem("nin") || "{}") || {};
      const ninValue = nin?.nin || nin?.data?.nin || "";
      navigate(`/insurance?nin=${encodeURIComponent(ninValue)}`);
      return;
    }

    setJoinLoading(true);
    try {
      const payload = {
        userId: Number(user?.id || storedUserData?.id),
        groupId: group.id,
      };

      const response = await postData("/api/group/join", payload);

      if (response?.status === true) {
        setShowPendingModal(true);
        await fetchAllData(); // Refresh APIs
      } else {
        alert(response?.message || "Unable to join group. Please try again.");
      }
    } catch (error) {
      console.error(error);
      alert("Network error. Please try again.");
    } finally {
      setJoinLoading(false);
    }
  };

  const handleClosePendingModal = () => {
    setShowPendingModal(false);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f6f7fb", color: "#1d2433" }}>
      <Navbar
        onMenuClick={() => setMobileNavOpen(true)}
        onNavigate={goTo}
        onLogout={logout}
        user={{ fullName: displayName, userType: role }}
      />
      <Sidebar
        role={role}
        activePath={location.pathname}
        onNavigate={goTo}
        mobileOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />
      <Box
        component="main"
        sx={{ ml: { xs: 0, md: "248px" }, p: { xs: 2, sm: 3, md: 5 } }}
      >
        <Box sx={{ maxWidth: 1440, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 800 }}
            >
              My Groups
            </Typography>
            <Typography sx={{ color: "#7a8394", mt: 0.7 }}>
              Manage the groups you have joined or requested to join.
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ minHeight: 190, display: "grid", placeItems: "center" }}>
              <CircularProgress sx={{ color: "#52057b" }} />
            </Box>
          ) : error ? (
            <Box
              sx={{
                p: 4,
                textAlign: "center",
                bgcolor: "#fff",
                border: "1px solid #e9ebf0",
                borderRadius: 2,
              }}
            >
              <Typography sx={{ color: "#c62828", fontWeight: 700 }}>
                Failed to load groups. Please try again.
              </Typography>
            </Box>
          ) : groups.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: "center",
                bgcolor: "#fff",
                border: "1px solid #e9ebf0",
                borderRadius: 2,
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>
                You have not joined any approved groups yet.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {groups.map((group, index) => {
                const active = isGroupActive(group);
                const joinStatus = getJoinStatus(group.id);

                return (
                  <Grid item xs={12} sm={6} lg={4} key={groupKey(group, index)}>
                    <Box
                      sx={{
                        bgcolor: "#fff",
                        border: "1px solid #e9ebf0",
                        borderRadius: 3,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      {/* Group Image Header */}
                      <Box
                        sx={{
                          height: 120,
                          bgcolor: "#f7f1fa",
                          display: "grid",
                          placeItems: "center",
                          position: "relative",
                        }}
                      >
                        {group.groupImage ? (
                          <Box
                            component="img"
                            src={group.groupImage}
                            alt={group.title}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <SearchOutlined
                            sx={{ fontSize: 48, color: "#52057b" }}
                          />
                        )}
                        <Chip
                          size="small"
                          label={active ? "Available" : "Inactive"}
                          color={active ? "success" : "default"}
                          sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            fontWeight: 700,
                          }}
                        />
                      </Box>

                      {/* Card Content */}
                      <Box sx={{ p: 2.5, flex: 1 }}>
                        <Typography
                          sx={{ fontWeight: 800, fontSize: 18, mb: 0.5 }}
                        >
                          {group.title || "Untitled group"}
                        </Typography>
                        <Typography
                          sx={{ color: "#7a8394", fontSize: 13, mb: 1.5 }}
                        >
                          {group.description || "No description available"}
                        </Typography>

                        {/* Meta Info */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            flexWrap: "wrap",
                            mb: 2,
                          }}
                        >
                          <Chip
                            label={formatCurrency(group.amount, symbol)}
                            size="small"
                            sx={{ bgcolor: "#f7f1fa", color: "#52057b" }}
                          />
                          <Chip
                            icon={<PeopleAlt />}
                            label={`${group.totalUsers || 1} members`}
                            size="small"
                            sx={{ bgcolor: "#f7f1fa", color: "#52057b" }}
                          />
                          <Chip
                            icon={<CalendarMonth />}
                            label={formatDate(group.createdAt)}
                            size="small"
                            sx={{ bgcolor: "#f7f1fa", color: "#52057b" }}
                          />
                        </Box>

                        {/* Monthly Fee */}
                        <Typography
                          sx={{ fontSize: 13, color: "#7a8394", mb: 1 }}
                        >
                          Monthly Fee:{" "}
                          <Box
                            component="span"
                            sx={{ fontWeight: 800, color: "#52057b" }}
                          >
                            NGN {Number(group.fee || 0).toLocaleString("en-US")}
                          </Box>
                        </Typography>
                      </Box>

                      {/* Action Footer */}
                      <Box
                        sx={{
                          p: 2,
                          borderTop: "1px solid #f0f1f4",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {joinStatus === "pending" ? (
                          <Chip
                            icon={<HourglassTop />}
                            label="Pending"
                            sx={{
                              bgcolor: "#fff3e0",
                              color: "#e65100",
                              fontWeight: 700,
                              width: "100%",
                              py: 1.5,
                            }}
                          />
                        ) : joinStatus === "joined" ? (
                          <Chip
                            icon={<CheckCircle />}
                            label="Joined"
                            sx={{
                              bgcolor: "#e8f5e9",
                              color: "#2e7d32",
                              fontWeight: 700,
                              width: "100%",
                              py: 1.5,
                            }}
                          />
                        ) : (
                          <Button
                            variant="contained"
                            disabled={!active || joinLoading}
                            startIcon={
                              joinLoading ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <GroupAddOutlined />
                              )
                            }
                            onClick={() => handleJoinClick(group)}
                            sx={{
                              bgcolor: "#52057b",
                              color: "#fff",
                              borderRadius: "12px",
                              width: "100%",
                              py: 1.2,
                              textTransform: "none",
                              fontWeight: 700,
                              "&:hover": {
                                bgcolor: "#3e055e",
                              },
                              "&.Mui-disabled": {
                                bgcolor: "#e0e0e0",
                                color: "#9e9e9e",
                              },
                            }}
                          >
                            Join Group
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Box>

      {/* ✅ Pending Modal */}
      <Dialog
        open={showPendingModal}
        onClose={handleClosePendingModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#52057b",
            fontWeight: 800,
            fontSize: "1.2rem",
            pt: 3,
            pb: 1,
          }}
        >
          You Account Is Still On Pending
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Typography
            sx={{
              color: "#555",
              fontSize: "0.95rem",
              lineHeight: 1.7,
              mb: 2,
            }}
          >
            You become a member of the group when the admin accepts your joining
            request. You will be notified once your request is accepted.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={handleClosePendingModal}
              sx={{
                color: "#52057b",
                fontWeight: 700,
                textTransform: "none",
                fontSize: "1rem",
              }}
            >
              Ok
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default JoinedGroups;
